import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import SaveBackdrop from '../../../components/saveBackdrop';
import { AppContext } from '../../../contexts/app-context';
import GlobalStyles from '../../../global-styles';
import { getContractOrderAccountSearch } from '../../../utils/helper';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormHelperText, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, TextField, Typography
} from '../../../utils/material-ui-core';

interface ConfirmProps {
  programKey: string;
  open: boolean;
  callActionButton: (confirmOutput: ConfirmOutput) => void;
}

interface Account {
  accountNumber: string,
  folderId: number,
  name: string,
  displayName: string,
  checked: boolean
}

const AccountSearch = ({ programKey, open, callActionButton }: ConfirmProps) => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { showAlert } = useContext(AppContext);

  const [searchText, setSearchText] = useState<string>("");
  const [searchList, setSearchList] = useState<Account[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<Account[]>([]);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [sortField, setSortField] = useState<keyof Account>('accountNumber');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');


  const handleSort = (field: keyof Account) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const getSortedList = () => {
    return [...searchList].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const handleCheckBoxChange = (selectedValue: Account) => {
    const updateData = searchList.map(rowItem => {
      if (rowItem.accountNumber === selectedValue.accountNumber) {
        rowItem.checked = !rowItem.checked;
      }
      return rowItem;
    });

    setSearchList(updateData);

    // Update selected accounts array
    const selected = updateData.filter(item => item.checked);
    setSelectedAccounts(selected);
  }


  const handleSelectAll = () => {
    const allSelected = searchList.every(item => item.checked);
    const updateData = searchList.map(rowItem => {
      rowItem.checked = !allSelected;
      return rowItem;
    });

    setSearchList(updateData);

    // Update selected accounts array
    const selected = updateData.filter(item => item.checked);
    setSelectedAccounts(selected);
  }

  const clearSelection = () => {
    const updateData = searchList.map(rowItem => {
      rowItem.checked = false;
      return rowItem;
    });

    setSearchList(updateData);
    setSelectedAccounts([]);
  }

  const search = async () => {
    setSearchList([]);

    setSaveInProgress(true);
    const apiResponse = await getContractOrderAccountSearch(programKey, searchText);

    if (apiResponse.success) {
      const accounts: Account[] = [];

      apiResponse.data.accounts.map(account => (
        accounts.push({
          folderId: account.folderId,
          accountNumber: account.accountNumber,
          name: account.name,
          displayName: `${account.name} - Folder ID: ${account.folderId} (${account.accountNumber})`,
          checked: false
        }))
      );

      setSearchList(accounts);
      setSelectedAccounts([]);

    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
    }
    setSaveInProgress(false);
  };


  const selectAccount = () => {
    const accountNumbers = selectedAccounts.map(account => account.accountNumber).join(',');
    callActionButton({ status: "select", data: accountNumbers })
  }

  const cancel = () => {
    callActionButton({ status: "cancel", data: null })
  }

  useEffect(() => {
    if (open) {
      setSearchText("");
      setSearchList([]);
      setSelectedAccounts([]);
    }
    else {
      setSearchText("");
      setSearchList([]);
      setSelectedAccounts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>

      <Dialog className={globalStyles.dialogContainer} open={open} maxWidth={"md"} fullWidth={true}>
        <DialogTitle id="input-dialog-title" className={globalStyles.dialogTitleContainer}>
          <Typography variant="subtitle1" component="div">
            Account Search
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <SaveBackdrop open={saveInProgress} />
          <DialogContentText className={pageStyles.contentText} component="div">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={8} md={9}  >
                <TextField
                  id="searchText"
                  name="searchText"
                  label="Search"
                  value={searchText}
                  disabled={selectedAccounts.length > 0}
                  onChange={(e) => {
                    setSearchText(e.target.value as string);
                    setSearchList([]);
                  }}
                />

                {searchText.length <= 2 ?
                  <FormHelperText className={globalStyles.error}>A minimum of 3 characters are required to search the account.</FormHelperText>
                  : null}

                {selectedAccounts.length > 0 ?
                  <FormHelperText style={{ color: '#1976d2' }}>Search is disabled while accounts are selected. Clear selection to search again.</FormHelperText>
                  : null}
              </Grid>
              <Grid item xs={12} sm={4} md={3} >
                <Box display="flex" alignContent="center" justifyContent="center">
                  <Button
                    disabled={searchText.length < 3 || selectedAccounts.length > 0}
                    color="primary"
                    variant="contained"
                    className={globalStyles.buttonDefault}
                    onClick={search}
                  >
                    Search
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box className={pageStyles.dialogContentContainer}>
                  {searchList.length > 0 && (
                    <TableContainer style={{ maxHeight: 'calc(100vh - 320px)' }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" style={{ backgroundColor: '#f5f5f5' }}>
                              <Checkbox
                                checked={searchList.length > 0 && searchList.every(item => item.checked)}
                                indeterminate={searchList.some(item => item.checked) && !searchList.every(item => item.checked)}
                                onChange={handleSelectAll}
                              />
                            </TableCell>
                            <TableCell style={{ backgroundColor: '#f5f5f5' }}>
                              <TableSortLabel
                                active={sortField === 'accountNumber'}
                                direction={sortField === 'accountNumber' ? sortDirection : 'asc'}
                                onClick={() => handleSort('accountNumber')}
                              >
                                Account Number
                              </TableSortLabel>
                            </TableCell>
                            <TableCell style={{ backgroundColor: '#f5f5f5' }}>
                              <TableSortLabel
                                active={sortField === 'folderId'}
                                direction={sortField === 'folderId' ? sortDirection : 'asc'}
                                onClick={() => handleSort('folderId')}
                              >
                                Folder ID
                              </TableSortLabel>
                            </TableCell>
                            <TableCell style={{ backgroundColor: '#f5f5f5' }}>
                              <TableSortLabel
                                active={sortField === 'name'}
                                direction={sortField === 'name' ? sortDirection : 'asc'}
                                onClick={() => handleSort('name')}
                              >
                                Name
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getSortedList().map((item, index) => (
                            <TableRow key={index} hover>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={item.checked}
                                  onChange={() => handleCheckBoxChange(item)}
                                />
                              </TableCell>
                              <TableCell>{item.accountNumber}</TableCell>
                              <TableCell>{item.folderId}</TableCell>
                              <TableCell>{item.name}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {searchList.length === 0 && searchText.length >= 3 && !saveInProgress && (
                    <Box textAlign="center" py={4}>
                      <Typography variant="body2" color="textSecondary">
                        No accounts found for your search criteria.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions>


          {selectedAccounts.length > 0 ?
            <Button onClick={selectAccount} color="secondary" variant="contained" className={globalStyles.buttonDefault}>
              Select ({selectedAccounts.length})
            </Button>
            : null
          }

          {selectedAccounts.length > 0 && (
            <Button
              onClick={clearSelection}
              color="default"
              className={globalStyles.buttonDefault}
            >
              <u>Clear Selection</u>
            </Button>
          )}

          <Button onClick={cancel} autoFocus className={globalStyles.buttonDefault}>
            <u>Close</u>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentText: {
      whiteSpace: "pre-wrap",
      "& .MuiChip-root": {
        maxWidth: "300px",
        borderRadius: theme.spacing(1),
        "& .MuiChip-label": {
          paddingTop: "4px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
    },
    dialogContentContainer: {
      minHeight: "400px",
      height: "calc(100vh - 320px)",
      overflow: "hidden",
      width: "100%",
      '& .MuiTableContainer-root': {
        maxHeight: 'calc(100vh - 380px)',
        overflow: 'auto',
      },
      '& .MuiTableHead-root .MuiTableCell-root': {
        fontWeight: 'bold',
        backgroundColor: '#f5f5f5 !important',
      },
      '& .MuiTableRow-hover:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      }
    }
  })
);

export default AccountSearch;
